import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBrandGithub } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { type GitHubReposPayload, parseGitHubReposPayload } from "./githubReposSchema";

interface GitHubReposRendererProps {
  block: StreamingBlock;
}

export const GitHubReposRenderer = (props: GitHubReposRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<GitHubReposPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseGitHubReposPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("github_repos_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().repos.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbBrandGithub class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">
                  {rt.t("github_repos_card.title", { account: data().account })}
                </span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().repos.length === 1
                    ? rt.t("github_repos_card.results_one")
                    : rt.t("github_repos_card.results", { count: String(data().repos.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().repos}>
                  {(repo) => (
                    <li class="flex items-center gap-1.5 px-3 py-2 text-sm">
                      <span class="truncate font-mono text-primary-text">{repo.fullName}</span>
                      <Show when={repo.private}>
                        <span class="shrink-0 rounded bg-inset px-1.5 py-0.5 text-xs text-muted-text">
                          {rt.t("github_repos_card.private")}
                        </span>
                      </Show>
                      <Show when={repo.archived}>
                        <span class="shrink-0 rounded bg-inset px-1.5 py-0.5 text-xs text-muted-text">
                          {rt.t("github_repos_card.archived")}
                        </span>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>

              <Show when={data().hasMore}>
                <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                  {rt.t("github_repos_card.has_more")}
                </div>
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
